import { keyframes } from '@emotion/react';
import {
  COLOR_PALETTE,
  Group,
  type GroupProps,
  OptimizeImage,
  Stack,
  Text,
  desktopFirstMediaQuery,
  desktopFirstQuery,
} from '@inflearn/ds-react-next';
import { useMediaQuery } from '@mantine/hooks';
import type { ReactNode } from 'react';

interface RadioCardWithoutIndicatorProps extends GroupProps {
  checked: boolean;
  imageSrc: string;
  label: string | ReactNode;
  description: string | ReactNode;
}

export const RadioCardWithoutIndicator = ({
  checked,
  imageSrc,
  label,
  description,
  ...props
}: RadioCardWithoutIndicatorProps) => {
  const isSmallerThanMobile = useMediaQuery(desktopFirstQuery.mobile);

  return (
    <Group
      gap={16}
      align="flex-start"
      wrap="nowrap"
      py={20}
      px={16}
      styles={{
        root: {
          cursor: 'pointer',
          flex: 1,
          transition: 'background-color 0.2s ease-in-out',
          borderRadius: 8,
          border: `1px solid ${checked ? COLOR_PALETTE.infgreen[7] : COLOR_PALETTE.gray[3]}`,
          backgroundColor: checked ? COLOR_PALETTE.infgreen[0] : 'transparent',

          '@media(hover: hover)': {
            '&:hover': {
              backgroundColor: checked ? 'auto' : COLOR_PALETTE.gray[0],
            },
          },

          [desktopFirstMediaQuery.mobile]: {
            padding: 16,
          },
        },
      }}
      {...props}
    >
      <OptimizeImage
        src={imageSrc}
        w={40 * 2}
        width={40}
        height={40}
        alt={`${label} 이미지`}
        css={{
          animation: checked ? `${smoothBounce} 0.4s ease-in-out` : 'none',
          transform: 'scale(1)',

          [desktopFirstMediaQuery.mobile]: {
            width: 32,
            height: 32,
          },
        }}
      />
      <Stack gap={4}>
        {typeof label === 'string' ? (
          <Text size="md" fw={600} lh={1.3}>
            {label}
          </Text>
        ) : (
          label
        )}
        {typeof description === 'string' ? (
          <Text
            fz={isSmallerThanMobile ? 'xs' : 'sm'}
            fw={isSmallerThanMobile ? 500 : 400}
            lh={1.4}
            c="gray.7"
          >
            {description}
          </Text>
        ) : (
          description
        )}
      </Stack>
    </Group>
  );
};

const smoothBounce = keyframes`
  0% { 
    transform: scale(1); 
  }

  50% { 
    transform: scale(1.25); 
  }
    
  100% { 
    transform: scale(1); 
  }
`;
